import { IS_SMALL_SCREEN } from '@Constants/LeftDrawer';
import { useScreenSize } from '@Hooks/useScreenSize';
import useCheckoutPresenter from '@Screens/Checkout/presenter';
import { formatCurrency } from '@Utils/currency';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import React, { useEffect } from 'react';
import styled, { css } from 'styled-components/native';

import OwnableTheme from '../../../assets/OwnableTheme';
import { Text } from '../../Common/Text';
import { useCheckoutContextState } from './CheckoutContextState';

import { useCheckoutStore } from '~stores/checkout';
import { useProductsStore } from '~stores/products';
import { Qty } from '~types/models-types';
import { ScreenSize } from '~types/views-types';

const SHOW_OPTION_SELECT_MODAL = false;

const StyledContainer = styled.View`
  width: 100%;
  margin-bottom: ${IS_SMALL_SCREEN ? 20 : 30}px;
  margin-top: 5px;
`;

const StyledSummaryContainer = styled.View<{
  size: ScreenSize;
}>`
  display: flex;
  flex-direction: ${(props) =>
    [ScreenSize.mobile, ScreenSize.thinmobile].includes(props.size)
      ? 'column'
      : 'row'};
  margin-bottom: 10px;
  padding-top: 40px;
  border-top-width: 2px;
  border-top-color: #dcdcdc;
  justify-content: space-between;
  align-items: center;
`;

const StyledDetailsContainer = styled.View<{
  size: ScreenSize;
}>`
  ${(props) =>
    ![ScreenSize.mobile, ScreenSize.thinmobile].includes(props.size) &&
    css`
      flex: 0.6;
    `};
  row-gap: 16px;
  border-radius: 10px;
  margin-right: ${(props) =>
    [ScreenSize.mobile, ScreenSize.thinmobile].includes(props.size)
      ? '0px'
      : '20px'}; ;
`;

const StyledRow = styled.View`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 12px;
`;

const StyledQuantityContainer = styled.View<{
  size: ScreenSize;
}>`
  ${(props) =>
    ![ScreenSize.mobile, ScreenSize.thinmobile].includes(props.size) &&
    css`
      align-items: flex-end;
    `};
  display: flex;
  row-gap: 16px;
  margin-bottom: 10px;
  margin-top: ${IS_SMALL_SCREEN ? 15 : 0}px;
`;

const StyledNumberInputContainer = styled.View`
  border: 1.5px solid ${OwnableTheme.colors.black};
  border-radius: 15px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: center;
  width: auto;
  margin-left: 15px;
`;

const StyledQuantityRow = styled.View`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;

const StyledQtyControl = styled.TouchableOpacity`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledQuantityInput = styled.TextInput`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  text-align: center;
  color: ${OwnableTheme.colors.black};
  font-size: ${IS_SMALL_SCREEN ? '10px' : '15px'};
  letter-spacing: ${IS_SMALL_SCREEN ? '0.3' : '0.45'};
  font-family: ${OwnableTheme.fonts.gilroy.regular};
  font-weight: 600;
`;

const StyledSizeInput = styled.Pressable`
  border: 1.5px solid ${OwnableTheme.colors.black};
  border-radius: 15px;
  padding: 12px 0;
  width: 180px;
  align-items: center;
  justify-content: center;
`;

const StyledOption = styled.Text`
  font-size: ${OwnableTheme.fontSizes[IS_SMALL_SCREEN ? 'xxxs' : 'xs']};
  font-weight: 500;
  font-family: ${OwnableTheme.fonts.gilroy.regular};
`;

const OrderSummary: FC = () => {
  const { orderSummary, quantity } = useCheckoutStore();

  const { updateQuantity, getOrderSummary, openProductOptionModal } =
    useCheckoutPresenter();
  const [size] = useScreenSize();
  const { currentProductSelectedOption, currentProduct } = useProductsStore();

  const { black } = OwnableTheme.colors;
  useEffect(() => {
    getOrderSummary(currentProduct?.id, quantity).then();
  }, [currentProductSelectedOption, quantity, currentProduct]);

  const { orderSummaryVisible } = useCheckoutContextState();
  useEffect(() => {
    orderSummaryVisible.set(true);
    return () => {
      orderSummaryVisible.set(false);
    };
  }, []);

  const textSize = IS_SMALL_SCREEN ? 'xxxs' : 'xs';
  const optionLabelText = currentProductSelectedOption
    ? currentProductSelectedOption?.optionName
    : '< Colour / size >';

  return (
    <StyledContainer>
      <Text
        gutterBottom={32}
        weight='bold'
        size={IS_SMALL_SCREEN ? 'xxs' : 'ml'}
        color={black}
      >
        Order Summary Details
      </Text>
      <StyledSummaryContainer size={size}>
        <StyledDetailsContainer size={size}>
          <StyledRow>
            <Text size={textSize} color={black}>
              Subtotal
            </Text>
            <Text size={textSize} color={black}>
              {formatCurrency(orderSummary?.subtotal)}
            </Text>
          </StyledRow>
          <StyledRow>
            <Text size={textSize} color={black}>
              Delivery
            </Text>
            <Text size={textSize} color={black}>
              {formatCurrency(orderSummary?.deliveryCharge)}
            </Text>
          </StyledRow>
          <StyledRow>
            <Text weight='bold' size={textSize} color={black}>
              Total
            </Text>
            <Text weight='bold' size={textSize} color={black}>
              {formatCurrency(orderSummary?.orderTotal)}
            </Text>
          </StyledRow>
        </StyledDetailsContainer>
        <StyledQuantityContainer size={size}>
          <StyledQuantityRow>
            <Text weight='bold' size={textSize} color={black}>
              Quantity
            </Text>
            <StyledNumberInputContainer>
              <StyledQtyControl
                onPress={() =>
                  updateQuantity(Qty.minus, orderSummary?.itemProductId)
                }
              >
                <Text color={black} size={IS_SMALL_SCREEN ? 'xxs' : 'm'}>
                  -
                </Text>
              </StyledQtyControl>
              <StyledQuantityInput
                editable={false}
                value={
                  quantity > 9 ? quantity.toString() : `0${quantity.toString()}`
                }
                keyboardType='numeric'
              />
              <StyledQtyControl
                onPress={() =>
                  updateQuantity(Qty.add, orderSummary?.itemProductId)
                }
              >
                <Text color={black} size={IS_SMALL_SCREEN ? 'xxs' : 'm'}>
                  +
                </Text>
              </StyledQtyControl>
            </StyledNumberInputContainer>
          </StyledQuantityRow>
          {SHOW_OPTION_SELECT_MODAL &&
            Boolean(currentProduct?.options?.length) && (
              <StyledSizeInput onPress={openProductOptionModal}>
                <StyledOption>{optionLabelText}</StyledOption>
              </StyledSizeInput>
            )}
        </StyledQuantityContainer>
      </StyledSummaryContainer>
    </StyledContainer>
  );
};

export default observer(OrderSummary);
